// Hook polling dùng chung cho job nền Film (đợt 260826, Epic 2 Phase 2).
//
// Trích xuất từ effect polling từng chỉ dùng riêng cho viết kịch bản (AdaptationTab.tsx) — tổng quát
// hoá để dùng lại cho crawl/quét (AdaptationSource.tsx). KHÔNG import antd — giữ đúng quy ước testable
// của thư mục này (xem đầu adaptationUtils.ts).
import { useEffect, useRef } from 'react';
import { adminTasksApi } from '../../services/api/adminTasks';
import type { AdminTask, AdminTaskDetail } from '../../types';
import { classifyFilmJobStatus } from './adaptationUtils';

export type FilmJobPollOutcome = 'completed' | 'failed' | 'unsupported' | 'timeout' | 'network-lost';

export interface UseFilmJobPollOptions<TDetail> {
  /** Gọi ĐÚNG 1 LẦN khi việc theo dõi kết thúc, dù xong/lỗi/hết giờ/mất mạng. */
  onTerminal: (outcome: FilmJobPollOutcome, task: AdminTask<TDetail> | null) => void | Promise<void>;
  /** Gọi ở MỌI lần poll thành công (kể cả lần cuối/terminal) — đọc `detail.progress` để hiện số real-time. */
  onTick?: (task: AdminTask<TDetail>) => void;
  intervalMs?: number;
  maxPolls?: number;
  maxNetworkFailures?: number;
}

/** `jobId = null` dừng theo dõi ngay (huỷ timer đang chạy nếu có). */
export function useFilmJobPoll<TDetail = AdminTaskDetail>(
  jobId: string | null,
  { onTerminal, onTick, intervalMs = 3000, maxPolls = 600, maxNetworkFailures = 3 }: UseFilmJobPollOptions<TDetail>,
): void {
  // Giữ bản mới nhất của onTerminal/onTick qua ref — component gọi hook thường truyền closure mới mỗi
  // lần render (đóng gói state/props hiện tại); nếu đưa thẳng vào deps của effect thì mỗi lần render lại
  // huỷ-dựng-lại timer đang đếm dở.
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;

  useEffect(() => {
    if (!jobId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let networkFailures = 0;
    let polls = 0;

    const finish = (outcome: FilmJobPollOutcome, task: AdminTask<TDetail> | null) => {
      if (!active) return;
      active = false;
      if (timer) clearTimeout(timer);
      onTerminalRef.current(outcome, task);
    };

    const poll = async () => {
      try {
        const response = await adminTasksApi.getTaskDetail<TDetail>(jobId);
        if (!active) return;
        networkFailures = 0;
        polls++;
        const task = response.data;
        onTickRef.current?.(task);
        const state = classifyFilmJobStatus(task.status);
        if (state === 'polling') {
          if (polls >= maxPolls) { finish('timeout', null); return; }
          timer = setTimeout(poll, intervalMs);
          return;
        }
        if (state === 'success') { finish('completed', task); return; }
        if (state === 'failure') { finish('failed', task); return; }
        finish('unsupported', task);
      } catch {
        if (!active) return;
        networkFailures++;
        if (networkFailures >= maxNetworkFailures) { finish('network-lost', null); return; }
        timer = setTimeout(poll, intervalMs);
      }
    };

    timer = setTimeout(poll, 1000);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, intervalMs, maxPolls, maxNetworkFailures]);
}
